import Button from "@material-ui/core/Button";
import {FormStatus, WithErrorAndProcessingStatus} from "../../../../models/shared";
import clsx from "clsx";
import {ArrowForwardIos} from "@material-ui/icons";
import Grid from "@material-ui/core/Grid";
import React, {useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {Machine} from "../../../../models/machine.models";
import MachineAvatar from "../../shared/MachineAvatar";
import {Typography} from "@material-ui/core";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import {OrganizationUnitSelectorDialog} from "../../../_shared/organization-unit-selector/OrganizationUnitSelectorDialog";
import {OrgUnit} from "../../../../models/organization";
import {useTranslation} from "react-i18next";
import {ValidationRow} from "../../../_shared/wizard/ValidationRow";
import {NavigationProps, WizardNavigation, WizardNavigationButton} from "../../../_shared/wizard/WizardNavigation";
import {moveMachines} from "../../../../api/machine-functions";
import * as _ from "lodash";

const useStyles = makeStyles((theme) => {
    const disabledOpacity = 0.6;

    return {
        to: {
            marginTop: theme.spacing(2),
            borderColor: (theme.palette as any).dark.line,
            border: 'thin solid',
            '&.Mui-disabled': {
                color: (theme.palette as any).dark.background,
                opacity: disabledOpacity
            }
        },
        toButtonError: {
            border: `thin ${theme.palette.error.light} solid`,
            color: theme.palette.error.light
        },

        dataContainer: {
            padding: theme.spacing(2),
        },
    }
})

interface MachineWithErrorAndProcessingStatus extends Machine, WithErrorAndProcessingStatus {
}

export interface OrgUnitWithProcessingStatus extends OrgUnit, WithErrorAndProcessingStatus {
}

interface Props extends NavigationProps {
    organizationId: number,
    onChangeDestinationOrgUnit: (orgUnit: OrgUnit) => void,
    form: {
        destinationOrgUnit: OrgUnit,
        machines: Machine[]
    }
}

export const Step2Validation: React.FC<Props> = ({organizationId, onChangeDestinationOrgUnit, form, ...props}) => {
    const classes = useStyles();
    const {t} = useTranslation()

    const [openSelectOrgUnit, setOpenSelectOrgUnit] = useState<boolean>(false);
    const [displayedForm, setDisplayedForm] = useState({
        destinationOrgUnit: {processingStatus: FormStatus.none} as OrgUnitWithProcessingStatus,
        machines: [] as MachineWithErrorAndProcessingStatus[]
    });

    useEffect(() => {
        setDisplayedForm({
            destinationOrgUnit: {
                ...form.destinationOrgUnit,
                processingStatus: FormStatus.none,
                errorMessage: ''
            } as OrgUnitWithProcessingStatus,
            machines: form.machines.map(m => ({...m, processingStatus: FormStatus.none, errorMessage: ''}))
        });
    }, [form]);


    const changeDestinationOrgUnit = () => setOpenSelectOrgUnit(true);

    const selectedOrgUnitChanged = async (orgUnit: OrgUnit) => {
        setDisplayedForm(p => ({
            ...p,
            destinationOrgUnit: {...orgUnit, processingStatus: FormStatus.none, errorMessage: ''}
        }));
        setOpenSelectOrgUnit(false);
    };

    const onSubmitChange = (changeTab: () => void) => {
        onChangeDestinationOrgUnit(displayedForm.destinationOrgUnit);
        changeTab();
    }

    const isCompleted = (status: any) => status === FormStatus.successful || status === FormStatus.failed;
    const allCompleted = displayedForm.machines.every((m: any) => isCompleted(m.processingStatus)) && isCompleted(displayedForm.destinationOrgUnit.processingStatus);


    const getDisabledButtons = (): Set<WizardNavigationButton> => {
        if (displayedForm.destinationOrgUnit.processingStatus === FormStatus.successful && !allCompleted) {
            return new Set([WizardNavigationButton.Previous, WizardNavigationButton.Close, WizardNavigationButton.Next, WizardNavigationButton.Save]);
        }
        return new Set<WizardNavigationButton>()
    }

    const onSave = async () => {
        setDisplayedForm(p => ({
            ...p,
            destinationOrgUnit: {...p.destinationOrgUnit, processingStatus: FormStatus.processing}
        }));

        if (!displayedForm.destinationOrgUnit.id) {
            setDisplayedForm(p => ({
                ...p,
                destinationOrgUnit: {...p.destinationOrgUnit, processingStatus: FormStatus.failed}
            }))
            return;
        }

        setDisplayedForm(p => ({
            ...p,
            destinationOrgUnit: {...p.destinationOrgUnit, processingStatus: FormStatus.successful}
        }));

        const updateIndexedProcessingDisplayedForm = (index: number) => (f: { destinationOrgUnit: OrgUnitWithProcessingStatus, machines: MachineWithErrorAndProcessingStatus[] }) => {
            f.machines[index].processingStatus = FormStatus.processing;
            return {...f, machines: f.machines};
        };

        const updateIndexedSuccessfulDisplayedForm = (index: number) => (f: { destinationOrgUnit: OrgUnitWithProcessingStatus, machines: MachineWithErrorAndProcessingStatus[] }) => {
            f.machines[index].processingStatus = FormStatus.successful;
            return {...f, machines: f.machines};
        };

        const updateIndexedFailedDisplayedForm = (index: number, e: any) => (f: { destinationOrgUnit: OrgUnitWithProcessingStatus, machines: MachineWithErrorAndProcessingStatus[] }) => {
            f.machines[index].processingStatus = FormStatus.failed;

            const errorType = _.get(e, 'response.data.error.type') || 'UnknownError';
            const msg = `_Common.Api.Messages.${errorType}`;
            f.machines[index].errorMessage = msg;

            return {...f, machines: f.machines};
        };

        const updateSuccessFul = (index: number) => () => {
            setDisplayedForm(updateIndexedSuccessfulDisplayedForm(index));
        };

        const updateFailed = (index: number) => (e: any) => {
            setDisplayedForm(updateIndexedFailedDisplayedForm(index, e));
        }

        for (const index in displayedForm.machines) {
            setDisplayedForm(updateIndexedProcessingDisplayedForm(parseInt(index)));

            await moveMachines({
                userId: displayedForm.machines[index].id,
                sourceOrgUnitId: displayedForm.machines[index].organizationUnitId,
                targetOrgUnitId: displayedForm.destinationOrgUnit.id
            })
                .then(updateSuccessFul(parseInt(index))).catch(updateFailed(parseInt(index)))
        }
    }

    const machineRow = (machine: MachineWithErrorAndProcessingStatus) =>
        <ValidationRow key={`validation-pro-${machine.id}`}
                       errorMessage={machine.errorMessage}
                       processingStatus={machine.processingStatus}>
            <Grid container item xs={11} spacing={4}>
                <Grid item> <MachineAvatar/> </Grid>
                <Grid item xs={4}>
                    <Typography variant="body1">{machine.friendlyName}</Typography>
                    <Typography variant="body2"
                                color={'textPrimary'}>{machine.email}</Typography>
                </Grid>
                <Grid item>
                    <Breadcrumbs>
                        <Typography variant="body1">
                            {`../${machine.organizationUnitName}`}
                        </Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>
        </ValidationRow>;

    return <Grid container spacing={3}>
        <ValidationRow isSingleItem={true}
                       processingStatus={displayedForm.destinationOrgUnit.processingStatus}
                       errorMessage={displayedForm.destinationOrgUnit.processingStatus === FormStatus.failed ? 'Machine.MoveMachines.Tabs.Validation.Destination.Error' : ''}>
            <Button
                disabled={displayedForm.machines.length > 0 && displayedForm.machines[0].processingStatus !== FormStatus.none}
                className={clsx(classes.to, displayedForm.destinationOrgUnit.processingStatus === FormStatus.failed && classes.toButtonError)}
                onClick={() => changeDestinationOrgUnit()}>
                {displayedForm.destinationOrgUnit.name ? displayedForm.destinationOrgUnit.name : t('Machine.MoveMachines.Tabs.Validation.Destination.Title')}
                <ArrowForwardIos/>
            </Button>
        </ValidationRow>
        <Grid container className={classes.dataContainer} spacing={4}>
            {displayedForm.machines.map((f: any) => machineRow(f))}
        </Grid>
        <WizardNavigation onChangeTab={props.onChangeTab}
                          onClose={props.onClose}
                          onSave={onSave}
                          index={props.index}
                          lastIndex={props.lastIndex}
                          allCompleted={allCompleted}
                          onSubmitChange={onSubmitChange}
                          disabledButtons={getDisabledButtons()}/>
        <OrganizationUnitSelectorDialog open={openSelectOrgUnit} onSelectOrgUnit={selectedOrgUnitChanged}
                                        onCancel={() => setOpenSelectOrgUnit(false)}
                                        initialSelectedOrgUnit={displayedForm.destinationOrgUnit ? {
                                            id: displayedForm.destinationOrgUnit.id,
                                            name: displayedForm.destinationOrgUnit.name
                                        } : undefined}
                                        organizationId={organizationId}/>
    </Grid>
}
